import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import {
  ProductsService,
  Product,
  Negotiation,
  ProductReservation,
  User,
  Comment,
  JournalService,
  UserLikes,
  CommentListResponse
} from '@vestiaire/api-sdk-angular';
import { MatDialog } from '@angular/material/dialog';
import { DiscussionsModalComponent } from '@app/shared/components/discussions/discussions-modal/discussions-modal.component';
import { IAppState } from '@app/store';
import { select, Store } from '@ngrx/store';
import * as fromUser from '@app/core/user/store/reducers';
import { Logger } from '@app/core/logging';
import { LikeTypes } from '@app/shared/components/like/like-types.enum';
import { Subject, BehaviorSubject } from 'rxjs';
import * as fromProduct from '@app/+product/store/reducers';
import { Discussions, IDiscussionModalData } from '@app/shared/components/discussions/discussion';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'vc-discussions',
  templateUrl: './discussions.component.html',
  styleUrls: ['./discussions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DiscussionsComponent extends Discussions implements OnInit, OnDestroy {
  @Input()
  commentType: string;

  @Input()
  product: Product;

  @Input()
  negotiations: Negotiation[];

  /**
   * If we are on an Edito page we pass the legacy Edito ID
   *
   * @type {string}
   * @memberof DiscussionsComponent
   */
  @Input()
  public editoId: string;

  /**
   * Tell if we want to show the like functionality
   * Default to true
   *
   * @type {boolean}
   * @memberof DiscussionsComponent
   */
  @Input()
  public showLikeFunctionality = true;

  public commentsList$ = new BehaviorSubject<Comment[]>([]);
  public allComments: Comment[];
  public nbDiscussions = 0;
  public isAuthenticated = false;
  public isFetching = false;
  public userInfo: User;
  public reservation: ProductReservation;
  public translateAll = false;
  public likedItem = [];
  public maxCommentsToDisplay = 3; // Max comments to display under the page

  private ngUnsubscribe = new Subject<void>();
  private modalWidth = '520px';

  constructor(
    public dialog: MatDialog,
    private journalService: JournalService,
    private productsService: ProductsService,
    private store: Store<IAppState>,
    private logger: Logger,
    private cd: ChangeDetectorRef
  ) {
    super();

    this.store.select('device').subscribe(
      res => (this.modalWidth = res.isLgUp ? '720px' : this.modalWidth),
      err => {}
    );
  }

  ngOnInit() {
    switch (this.commentType) {
      case 'product':
        if (this.product && this.product.id !== '') {
          this._getProductListComments();
        }
        break;
      case 'edito':
        this._getEditoListComments(this.editoId);
        break;
    }
    this._getIsAuthenticated();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public addNewComment(event) {
    if (event.message) {
      this.allComments.push(event);
      this._updateLastDiscussions();
    }
  }

  public toggleTranslateAll() {
    this.translateAll = !this.translateAll;
  }

  public openDiscussionModal() {
    const dialogRef = this.dialog.open(DiscussionsModalComponent, {
      position: { right: '0', top: '0', bottom: '0' },
      width: this.modalWidth,
      panelClass: ['discussionsModalPanel'],
      height: '100%',
      data: {
        allComments: this.allComments,
        commentType: this.commentType,
        isAuthenticated: this.isAuthenticated,
        product: this.product,
        negotiations: this.negotiations,
        reservation: this.reservation,
        user: this.userInfo,
        editoId: this.editoId,
        showLikeFunctionality: this.showLikeFunctionality
      } as IDiscussionModalData
    });

    dialogRef.componentInstance.sendMessage.subscribe(
      res => {
        this.allComments.push(res);
        this._updateLastDiscussions();
        dialogRef.close();
      },
      err => {
        //
      }
    );
  }

  private _getIsAuthenticated() {
    this.store.pipe(select(fromUser.isAuthenticated), takeUntil(this.ngUnsubscribe)).subscribe(
      res => {
        if (res) {
          this.isAuthenticated = true;
          this._getUserInfos();
        }
      },
      error => {
        this.logger.error('[DISCUSSIONS] _getIsAuthenticated: ' + error);
      }
    );
  }

  private _getLikedItems(itemType: LikeTypes) {
    this.store.pipe(select(fromUser.getLikedItems), takeUntil(this.ngUnsubscribe)).subscribe(
      (likedItems: UserLikes) => {
        if (likedItems && likedItems[itemType]) {
          this.likedItem = likedItems[itemType];
        }
      },
      error => {
        this.logger.error('Error fetching getLikedItems in DiscussionsComponent :', error);
      }
    );
  }

  private _getReservation() {
    this.store.pipe(select(fromProduct.selectReservationState), takeUntil(this.ngUnsubscribe)).subscribe(
      state => {
        this.reservation = state && state.productId === this.product.id ? state.status : null;
      },
      error => {}
    );
  }

  private _getUserInfos() {
    this.store.pipe(select(fromUser.getUser), takeUntil(this.ngUnsubscribe)).subscribe(
      res => {
        this.userInfo = res;
      },
      err => {
        this.logger.error('Error fetching _getUserInfos in DiscussionsComponent :', err);
      }
    );
  }

  /**
   * This function only store the last 3 comments
   * to display them
   *
   * @private
   * @memberof DiscussionsComponent
   */
  private _updateLastDiscussions() {
    this.nbDiscussions = this.allComments.length;
    if (this.nbDiscussions > this.maxCommentsToDisplay) {
      this.commentsList$.next(
        this.allComments.slice(this.nbDiscussions - this.maxCommentsToDisplay, this.nbDiscussions)
      );
    } else {
      this.commentsList$.next(this.allComments);
    }
    this.cd.markForCheck();
  }

  /**
   * Fetch the product Service to get the list of comments related to the
   * product ID.
   *
   * @private
   * @memberof DiscussionsComponent
   */
  private _getProductListComments() {
    this.isFetching = true;
    this.productsService.listProductComents(this.product.id).subscribe({
      next: resp => {
        this._listCommentsResp(resp);
        this._getReservation();
      },
      error: error => this.logger.error("Can't upload product discussions", error),
      complete: () => (this.isFetching = false)
    });
  }

  /**
   * Fetch the edito Service to get the list of comments related to the
   * edito that match the pageId.
   *
   * @private
   * @param {string} pageId
   * @memberof DiscussionsComponent
   */
  private _getEditoListComments(pageId: string) {
    this.isFetching = true;
    this.journalService.listEditoComents(pageId, this.maxCommentsToDisplay).subscribe({
      next: resp => this._listCommentsResp(resp),
      error: error => this.logger.error("Can't upload edito discussions", error),
      complete: () => (this.isFetching = false)
    });
  }

  /**
   * One function to fetch the getComments response
   *
   * @private
   * @param {CommentListResponse} resp
   * @memberof DiscussionsComponent
   */
  private _listCommentsResp(resp: CommentListResponse) {
    if (resp && resp.data) {
      this.allComments = resp.data;
      this._updateLastDiscussions();
      this._getLikedItems(LikeTypes.COMMENTS);
    }
  }
}
